import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaPlaneDeparture,
  FaHotel,
  FaMapMarkedAlt,
  FaUsers,
  FaArrowRight,
  FaCheckCircle,
} from "react-icons/fa";
import { Sparkles } from "lucide-react";

import img1 from "../../Assets/img/11.jpg";
import img2 from "../../Assets/img/12.jpg";
import img3 from "../../Assets/img/3.jpg";
import img4 from "../../Assets/img/13.webp";

const servicesData = [
  {
    number: '01', title: 'Hotel Booking', icon: FaHotel,
    bg: 'bg-white',
    image: img1,
    tag: 'Stay Planning',
    description: [
      'We offer a comprehensive range of hotel booking options, from luxury heritage stays to budget-friendly accommodations.',
      'Ensure your comfort with verified properties offering top-tier hospitality and proximity to Khajuraho Routess.',
    ],
    stat: { value: 'Top', label: 'Rated Stays' },
  },
  {
    number: '02', title: 'Event Management', icon: FaUsers,
    bg: 'bg-[#fbf4e8]',
    image: img2,
    tag: 'Corporate & Personal',
    description: [
      'Expert event management services for destination weddings, corporate retreats, and cultural gatherings in Khajuraho.',
      'From venue selection to decor and guest hospitality, we manage everything flawlessly.',
    ],
    stat: { value: '100%', label: 'Success Rate' },
  },
  {
    number: '03', title: 'Tour Packages', icon: FaMapMarkedAlt,
    bg: 'bg-white',
    image: img3,
    tag: 'Curated Trips',
    description: [
      'Discover the magic of Khajuraho with our meticulously crafted tour packages for families, couples, and groups.',
      'We handle the itinerary, local transport, and sightseeing to give you a hassle-free and enriching experience.',
    ],
    stat: { value: '50+', label: 'Tours Planned' },
  },
  {
    number: '04', title: 'Flight Booking', icon: FaPlaneDeparture,
    bg: 'bg-[#fbf4e8]',
    image: img4,
    tag: 'Travel Assistance',
    description: [
      'Seamless flight booking services to and from major domestic and international airports.',
      'Let us handle the ticketing process and schedule management so you can focus on enjoying your journey.',
    ],
    stat: { value: '24/7', label: 'Ticketing Support' },
  },
];

const ServiceBlock = ({ step, index }) => {
  const Icon = step.icon;
  const isEven = index % 2 === 0;

  return (
    <section className={`py-16 md:py-24 ${step.bg}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {/* TEXT BLOCK */}
          <motion.div 
            initial={{ opacity: 0, x: isEven ? -50 : 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className={`flex flex-col gap-6 ${!isEven ? 'lg:order-2' : ''}`}
          >
            {/* <div className="flex items-center gap-3">
              <span className="block w-10 h-0.5 bg-orange-main" />
              <span className="text-xs font-bold uppercase tracking-widest text-orange-main">
                Service {step.number}
              </span>
            </div> */}

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 leading-tight uppercase">
              {step.title}
            </h2>

            <div className="inline-flex items-center gap-2 self-start px-4 py-1.5 rounded-full border border-orange-main/30 bg-orange-main/5">
              <Icon size={12} className="text-orange-main" />
              <span className="text-xs font-bold uppercase tracking-wider text-orange-main">
                {step.tag}
              </span>
            </div>

            <div className="flex flex-col gap-3 max-w-lg">
              {step.description.map((desc, i) => (
                <p key={i} className="text-base text-gray-600 leading-relaxed">
                  {desc}
                </p>
              ))}
            </div>

            <div className="flex items-center gap-6 mt-4">
              {/* <div className="flex flex-col items-center px-5 py-3 rounded-xl border border-orange-main/30 bg-orange-main/5 min-w-[100px]">
                <span className="text-2xl font-black text-orange-main leading-tight">
                  {step.stat.value}
                </span>
                <span className="text-[10px] font-bold uppercase tracking-wider text-orange-main mt-1">
                  {step.stat.label}
                </span>
              </div> */}

              <Link to="/contact">
                <motion.div
                  whileHover={{ x: 6 }}
                  className="flex items-center gap-2 text-sm font-bold text-gray-900 cursor-pointer"
                >
                  Book Now
                  <FaArrowRight size={14} className="text-orange-main" />
                </motion.div>
              </Link>
            </div>
          </motion.div>

          {/* IMAGE BLOCK */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className={`relative rounded-3xl overflow-hidden shadow-2xl h-[300px] sm:h-[400px] lg:h-[500px] ${!isEven ? 'lg:order-1' : ''}`}
          >
            <img
              src={step.image}
              alt={step.title}
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
            />
            
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

            <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
              {/* <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/90 backdrop-blur-md shadow-sm">
                <span className="text-[10px] font-black uppercase tracking-widest text-gray-800">
                  Step
                </span>
                <span className="text-lg font-black text-orange-main leading-none">
                  {step.number}
                </span>
              </div> */}

              
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

const Services = () => {
  useEffect(() => {
    document.title = 'Services | Khajuraho Routes';
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="w-full bg-white">
      
      {/* ─── HERO ──────────────────────────────────────────── */}
      <section className="relative w-full h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${img1})` }}
        />
        <div className="absolute inset-0 bg-black/60" />
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-4 max-w-4xl mx-auto"
        >
          <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-orange-main/50 bg-orange-main/20 backdrop-blur-md">
            <Sparkles size={14} className="text-orange-main" />
            <span className="text-xs font-bold uppercase tracking-widest text-orange-main">
              Khajuraho Routes
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight mb-6">
            Services Built Around <br />
            <span className="text-orange-main italic">Your Journey</span>
          </h1>

          <p className="text-base md:text-lg text-gray-300 font-medium max-w-2xl mx-auto mb-10 leading-relaxed">
            From itinerary planning to transport, stays, and local guidance, we shape every part of your trip with care, clarity, and dependable support.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/tours"
              className="bg-orange-main text-white px-8 py-3.5 rounded-xl font-bold text-sm shadow-lg shadow-orange-main/30 hover:bg-orange-600 transition-colors"
            >
              Explore Packages
            </Link>
            <Link
              to="/contact"
              className="bg-white/10 backdrop-blur-md text-white border border-white/20 px-8 py-3.5 rounded-xl font-bold text-sm hover:bg-white/20 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </motion.div>
      </section>

      {/* ─── ZIG ZAG SERVICES SECTION ──────────────────────── */}
      <div className="w-full">
        {servicesData.map((step, idx) => (
          <ServiceBlock key={idx} step={step} index={idx} />
        ))}
      </div>

    </main>
  );
};

export default Services;
